"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight, Loader2, X, ImageOff } from "lucide-react";
import { useTranslations } from "next-intl";
import {
  getDisplayImageUrl,
  getSyncDisplayUrlHint,
  shouldBypassNextImageOptimizer,
} from "@/lib/heic-images";

interface Props {
  images: string[];
  title: string;
  /** Передаётся из page.tsx: первое изображение — LCP, нужен fetchpriority=high */
  priority?: boolean;
}

const navBtnMain =
  "pointer-events-auto absolute top-1/2 z-40 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-md opacity-0 backdrop-blur-sm transition-all hover:scale-110 group-hover:opacity-100 dark:bg-gray-900/90 max-sm:opacity-100";

const navBtnLightbox =
  "absolute z-50 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20";

function Skeleton() {
  return (
    <div className="pointer-events-none absolute inset-0 z-30 animate-pulse bg-gray-200 dark:bg-gray-800">
      <div className="flex h-full w-full flex-col items-center justify-center gap-2">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400 dark:text-gray-600" aria-hidden="true" />
      </div>
    </div>
  );
}

export default function CarGallery({ images, title, priority = false }: Props) {
  const t = useTranslations("CarDetailPage");

  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [resolvedImages, setResolvedImages] = useState<string[]>(() =>
    images.map((src) => getSyncDisplayUrlHint(src)),
  );
  const [pendingResolveIndices, setPendingResolveIndices] = useState<Set<number>>(() => {
    const pending = new Set<number>();
    images.forEach((src, i) => {
      if (!getSyncDisplayUrlHint(src)) pending.add(i);
    });
    return pending;
  });
  const [isMainLoading, setIsMainLoading] = useState(() => !getSyncDisplayUrlHint(images[0] ?? ""));
  const [isLightboxLoading, setIsLightboxLoading] = useState(false);
  const [failedIndices, setFailedIndices] = useState<Set<number>>(new Set());

  const mainImageRef = useRef<HTMLImageElement | null>(null);
  /** Ref для кнопки, открывшей лайтбокс — нужен для возврата фокуса при закрытии */
  const openTriggerRef = useRef<HTMLElement | null>(null);
  /** Ref для первой кнопки внутри лайтбокса — фокус переходит сюда при открытии */
  const lightboxCloseRef = useRef<HTMLButtonElement | null>(null);
  /** Сохраняем overflow до открытия лайтбокса, чтобы корректно восстановить */
  const prevBodyOverflow = useRef<string>("");
  /** Ref-зеркало lightbox для колбэков без пересоздания через useCallback */
  const lightboxRef = useRef(lightbox);
  useEffect(() => { lightboxRef.current = lightbox; }, [lightbox]);

  const len = images.length;
  const currentSrc = resolvedImages[active] ?? "";
  const isFailed = failedIndices.has(active);
  const showSkeleton = (isMainLoading || pendingResolveIndices.has(active)) && !isFailed;

  // ─── 1. Резолв HEIC-изображений ───────────────────────────────────────────
  // Разделено на два эффекта: инициализация при смене массива и
  // приоритизация соседей при смене слайда. Не перезапускаем весь цикл резолва.

  useEffect(() => {
    let mounted = true;

    const initAll = async () => {
      const hints = images.map((src) => getSyncDisplayUrlHint(src));
      setResolvedImages(hints);
      setPendingResolveIndices(new Set(images.map((_, i) => i)));
      setFailedIndices(new Set());

      for (let i = 0; i < images.length; i++) {
        if (!mounted) return;
        try {
          const u = await getDisplayImageUrl(images[i]);
          if (!mounted) return;
          setResolvedImages((prev) => {
            const next = [...prev];
            next[i] = u;
            return next;
          });
        } catch {
          // оставляем hint-URL, ошибка будет поймана onError изображения
        } finally {
          setPendingResolveIndices((prev) => {
            const next = new Set(prev);
            next.delete(i);
            return next;
          });
        }
      }
    };

    initAll();
    return () => { mounted = false; };
  }, [images]); // eslint-disable-line react-hooks/exhaustive-deps

  /** При смене слайда резолвим соседей раньше остальных (prefetch) */
  useEffect(() => {
    if (!images.length) return;
    const neighbors = [
      (active + 1) % images.length,
      (active - 1 + images.length) % images.length,
    ].filter((i) => i !== active);

    let mounted = true;
    const prefetch = async () => {
      for (const i of neighbors) {
        if (!mounted) return;
        if (!pendingResolveIndices.has(i)) continue; // уже разрешён
        try {
          const u = await getDisplayImageUrl(images[i]);
          if (!mounted) return;
          setResolvedImages((prev) => {
            const next = [...prev];
            next[i] = u;
            return next;
          });
          setPendingResolveIndices((prev) => {
            const next = new Set(prev);
            next.delete(i);
            return next;
          });
        } catch { /* ignore */ }
      }
    };
    prefetch();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Если массив сократился — сбрасываем индекс на 0
  useEffect(() => {
    if (len && active >= len) setActive(0);
  }, [len, active]);

  // При смене URL активного слайда убираем «ошибку» (HEIC мог догрузиться)
  useEffect(() => {
    setFailedIndices((prev) => {
      if (!prev.has(active)) return prev;
      const next = new Set(prev);
      next.delete(active);
      return next;
    });
  }, [active, currentSrc]);

  // Показываем скелет при смене src — но не если браузер уже закэшировал
  useEffect(() => {
    if (!currentSrc) return;
    if (mainImageRef.current?.complete) {
      setIsMainLoading(false);
    } else {
      setIsMainLoading(true);
    }
  }, [currentSrc]);

  useEffect(() => {
    if (lightbox && currentSrc) setIsLightboxLoading(true);
  }, [lightbox, currentSrc]);

  // ─── 2. Управление лайтбоксом ─────────────────────────────────────────────
  const openLightbox = useCallback((trigger: HTMLElement) => {
    openTriggerRef.current = trigger;
    prevBodyOverflow.current = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setIsLightboxLoading(true);
    setLightbox(true);
  }, []);

  const closeLightbox = useCallback(() => {
    document.body.style.overflow = prevBodyOverflow.current;
    setLightbox(false);
    // Возвращаем фокус на элемент, открывший лайтбокс
    requestAnimationFrame(() => {
      (openTriggerRef.current as HTMLElement | null)?.focus();
    });
  }, []);

  // Фокус внутрь лайтбокса при открытии
  useEffect(() => {
    if (lightbox) {
      requestAnimationFrame(() => lightboxCloseRef.current?.focus());
    }
  }, [lightbox]);

  // ─── 3. Навигация ─────────────────────────────────────────────────────────
  const handleNext = useCallback((e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (len <= 1) return;
    setIsMainLoading(true);
    if (lightboxRef.current) setIsLightboxLoading(true);
    setActive((prev) => (prev + 1) % len);
  }, [len]);

  const handlePrev = useCallback((e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (len <= 1) return;
    setIsMainLoading(true);
    if (lightboxRef.current) setIsLightboxLoading(true);
    setActive((prev) => (prev - 1 + len) % len);
  }, [len]);

  const handleSelect = useCallback((i: number, e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (i === active) return;
    setIsMainLoading(true);
    setActive(i);
  }, [active]);

  // ─── 4. Клавиатура в лайтбоксе ────────────────────────────────────────────
  useEffect(() => {
    if (!lightbox) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowRight") handleNext();
      else if (e.key === "ArrowLeft") handlePrev();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [lightbox, handleNext, handlePrev, closeLightbox]);

  // ─── 5. Обработчики изображений ───────────────────────────────────────────
  const onMainLoad = useCallback((index: number) => {
    if (index !== active) return; // запоздавшее событие старого слайда
    setIsMainLoading(false);
    setFailedIndices((prev) => {
      if (!prev.has(index)) return prev;
      const next = new Set(prev); next.delete(index); return next;
    });
  }, [active]);

  const onMainError = useCallback((index: number) => {
    setFailedIndices((prev) => new Set(prev).add(index));
    setIsMainLoading(false);
  }, []);

  const onLightboxLoad = useCallback(() => setIsLightboxLoading(false), []);
  const onLightboxError = useCallback((index: number) => {
    setFailedIndices((prev) => new Set(prev).add(index));
    setIsLightboxLoading(false);
  }, []);

  // ─── Рендер ───────────────────────────────────────────────────────────────
  if (!len) {
    return (
      <div
        className="flex h-72 items-center justify-center rounded-2xl bg-gray-100 dark:bg-gray-800 md:h-[420px]"
        aria-label={t("galleryEmpty")}
      >
        <ImageOff className="h-12 w-12 text-gray-300" aria-hidden="true" />
      </div>
    );
  }

  const isBlob = currentSrc.startsWith("blob:");
  const bypassOptimizer = shouldBypassNextImageOptimizer(currentSrc);

  return (
    <>
      <div className="flex flex-col gap-3">
        {/* ── Главный слайд ── */}
        <div
          role="group"
          aria-roledescription="carousel"
          aria-label={t("galleryAriaLabel", { title })}
          className="group relative h-72 overflow-hidden rounded-2xl bg-gray-100 dark:bg-gray-800 md:h-[420px]"
        >
          {showSkeleton && <Skeleton />}

          {isFailed ? (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-gray-200 dark:bg-gray-900">
              <ImageOff className="h-8 w-8 text-gray-400" aria-hidden="true" />
              <span className="text-xs text-gray-500">{t("galleryLoadErrorFallback")}</span>
            </div>
          ) : (
            currentSrc && (
              <button
                type="button"
                aria-label={t("galleryOpenLightbox", { n: active + 1, total: len })}
                className="absolute inset-0 z-10 cursor-zoom-in focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E63946]"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!showSkeleton) openLightbox(e.currentTarget);
                }}
              >
                <span className="sr-only">{t("galleryOpenLightbox", { n: active + 1, total: len })}</span>
              </button>
            )
          )}

          {currentSrc && !isFailed && (
            isBlob ? (
              <img
                ref={mainImageRef}
                src={currentSrc}
                alt={t("galleryPhotoAlt", { title, n: active + 1 })}
                onLoad={() => onMainLoad(active)}
                onError={() => onMainError(active)}
                loading={priority && active === 0 ? "eager" : "lazy"}
                fetchPriority={priority && active === 0 ? "high" : "auto"}
                decoding="async"
                className={`h-full w-full object-cover transition-opacity duration-500 ${showSkeleton ? "opacity-0" : "opacity-100"}`}
              />
            ) : (
              <Image
                key={currentSrc}
                ref={(img) => { mainImageRef.current = img; }}
                src={currentSrc}
                alt={t("galleryPhotoAlt", { title, n: active + 1 })}
                fill
                priority={priority && active === 0}
                loading={priority && active === 0 ? "eager" : "lazy"}
                fetchPriority={priority && active === 0 ? "high" : "auto"}
                sizes="(max-width: 1024px) 100vw, 800px"
                unoptimized={bypassOptimizer}
                onLoad={() => onMainLoad(active)}
                onError={() => onMainError(active)}
                className={`object-cover transition-opacity duration-500 ${showSkeleton ? "opacity-0" : "opacity-100"}`}
              />
            )
          )}

          {len > 1 && (
            <>
              <button
                type="button"
                onClick={handlePrev}
                className={`${navBtnMain} left-3`}
                aria-label={t("galleryPrev")}
              >
                <ChevronLeft size={20} className="text-gray-700 dark:text-gray-200" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={handleNext}
                className={`${navBtnMain} right-3`}
                aria-label={t("galleryNext")}
              >
                <ChevronRight size={20} className="text-gray-700 dark:text-gray-200" aria-hidden="true" />
              </button>
              {/* aria-live: при навигации счётчик озвучивается скринридером */}
              <div
                aria-live="polite"
                aria-atomic="true"
                className="absolute bottom-3 right-4 z-20 rounded-full bg-black/60 px-3 py-1 text-[11px] font-medium text-white backdrop-blur-md"
              >
                {active + 1} / {len}
              </div>
            </>
          )}
        </div>

        {/* ── Миниатюры ── */}
        {len > 1 && (
          <div
            role="group"
            aria-label={t("galleryThumbnails")}
            className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide"
          >
            {images.map((_, i) => {
              const thumbUrl = resolvedImages[i];
              const isActive = i === active;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={(e) => handleSelect(i, e)}
                  aria-label={t("galleryThumbLabel", { n: i + 1, total: len })}
                  aria-pressed={isActive}
                  aria-current={isActive ? "true" : undefined}
                  className={`relative h-16 w-24 flex-shrink-0 overflow-hidden rounded-xl border-2 transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E63946] ${
                    isActive
                      ? "border-[#E63946] ring-2 ring-[#E63946]/10 opacity-100"
                      : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                >
                  {!thumbUrl || pendingResolveIndices.has(i) ? (
                    <div className="h-full w-full animate-pulse bg-gray-200 dark:bg-gray-800" aria-hidden="true" />
                  ) : thumbUrl.startsWith("blob:") ? (
                    <img
                      src={thumbUrl}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Image
                      src={thumbUrl}
                      alt=""
                      aria-hidden="true"
                      fill
                      sizes="100px"
                      loading="lazy"
                      unoptimized={shouldBypassNextImageOptimizer(thumbUrl)}
                      className="object-cover"
                    />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Лайтбокс (портал через условный рендер) ── */}
      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t("galleryLightboxLabel", { title })}
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/95"
          // клик по фону закрывает лайтбокс
          onPointerDown={(e) => {
            if (e.target === e.currentTarget) closeLightbox();
          }}
        >
          {/* Кнопка закрытия — первый фокусируемый элемент */}
          <button
            ref={lightboxCloseRef}
            type="button"
            className="absolute right-6 top-6 z-[110] rounded-full bg-white/10 p-2 text-white/70 transition-colors hover:bg-white/20 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/50"
            onClick={closeLightbox}
            aria-label={t("galleryCloseLightbox")}
          >
            <X size={28} aria-hidden="true" />
          </button>

          <div
            className="relative flex h-full w-full items-center justify-center p-4 md:p-12"
            // stopPropagation только на внутреннем контейнере, не на фоне
            onClick={(e) => e.stopPropagation()}
          >
            {isLightboxLoading && (
              <Loader2
                className="absolute z-[120] h-12 w-12 animate-spin text-white/50"
                aria-hidden="true"
              />
            )}

            {len > 1 && (
              <>
                <button
                  type="button"
                  className={`${navBtnLightbox} left-4 md:left-8`}
                  onClick={handlePrev}
                  aria-label={t("galleryPrev")}
                >
                  <ChevronLeft size={32} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className={`${navBtnLightbox} right-4 md:right-8`}
                  onClick={handleNext}
                  aria-label={t("galleryNext")}
                >
                  <ChevronRight size={32} aria-hidden="true" />
                </button>
              </>
            )}

            <div className="relative h-full w-full max-w-6xl">
              {currentSrc && (
                isBlob ? (
                  <img
                    src={currentSrc}
                    alt={t("galleryPhotoAlt", { title, n: active + 1 })}
                    onLoad={onLightboxLoad}
                    onError={() => onLightboxError(active)}
                    className={`h-full w-full object-contain transition-opacity duration-300 ${isLightboxLoading ? "opacity-0" : "opacity-100"}`}
                  />
                ) : (
                  <Image
                    src={currentSrc}
                    alt={t("galleryPhotoAlt", { title, n: active + 1 })}
                    fill
                    sizes="100vw"
                    unoptimized={bypassOptimizer}
                    className={`object-contain transition-opacity duration-300 ${isLightboxLoading ? "opacity-0" : "opacity-100"}`}
                    onLoad={onLightboxLoad}
                    onError={() => onLightboxError(active)}
                  />
                )
              )}
            </div>
          </div>

          {/* Индикатор слайдов */}
          {len > 1 && (
            <div
              className="absolute bottom-8 flex gap-2"
              aria-hidden="true"
            >
              {images.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${i === active ? "w-6 bg-white" : "w-1.5 bg-white/30"}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}